import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Eye, EyeSlash } from "phosphor-react";
import { toast } from "react-toastify";

import AppModal from "@shared/ui/Modal";
import Button from "@shared/ui/Button";
import { Input } from "@shared/ui/Inputs";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";

import { LOGIN, LOGIN_FAILED } from "#/src/constants";
import { registerMixpanelEvent } from "#/src/utils/_mixpanel";

import { LoginPortalModalProps, LoginFormValues } from "./types";
import { useLoginPortal, LoginCredentials } from "./hooks";

const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const LoginPortalModal = ({
  isOpen = false,
  onClose,
  onRequestClick,
  onForgotPasswordClick,
  organizationId,
  onLoginSuccess,
  portalName,
}: LoginPortalModalProps) => {
  const { messages: msg, formatMessage } = useIntl();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useLoginPortal();
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues>();

  const onSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    const { email: userEmail } = values;

    registerMixpanelEvent(LOGIN, {
      oem: window.location.pathname.split("/").slice(-1).join("").split("?")[0],
      userEmail,
    });

    const loginCredentials: LoginCredentials = {
      email: values.email,
      password: values.password,
      organizationId: organizationId || "",
    };

    login(loginCredentials, {
      onSuccess: (data) => {
        setLoading(false);
        onLoginSuccess?.(data.redirectUrl);

        if (data.message) {
          toast(
            <Toast
              message={data.message}
              type={data.isError ? TOAST_TYPES.ERROR : TOAST_TYPES.SUCCESS}
            />,
            { closeButton: false },
          );
        } else if (data.redirectUrl?.includes("connection-requests")) {
          toast(
            <Toast
              message={msg.customerPortals["requestSuccess"]}
              type={TOAST_TYPES.SUCCESS}
            />,
            { closeButton: false },
          );
        }
      },
      onError: (e) => {
        setLoading(false);
        toast(
          <Toast
            message={
              e?.message ||
              (msg.customerPortals["welcome"]["wrongCredentials"] as string)
            }
            type={TOAST_TYPES.ERROR}
          />,
          { closeButton: false },
        );
        setError("root.random", {
          type: "random",
          message: e?.message,
        });
        registerMixpanelEvent(LOGIN_FAILED, {
          oem: window.location.pathname
            .split("/")
            .slice(-1)
            .join("")
            .split("?")[0],
          userEmail,
        });
      },
    });
  };

  const onError = () => {
    setLoading(false);
  };

  const emailField = register("email", {
    required: {
      value: true,
      message: msg?.common["errors"]["emailRequired"],
    },
    pattern: {
      value: emailPattern,
      message: msg?.login["email"]["invalidEmail"],
    },
  });

  const passwordField = register("password", {
    required: {
      value: true,
      message: msg?.login["password"]["invalidPassword"],
    },
  });

  const resetForm = useCallback(() => {
    clearErrors("email");
    clearErrors("password");
    setShowPassword(false);
  }, [clearErrors]);

  return (
    <AppModal
      isOpen={isOpen}
      title={msg.customerPortals["welcome"]["login"] as string}
      handleClose={
        loading
          ? undefined
          : () => {
              resetForm();
              onClose?.();
            }
      }
      maxWidth="!max-w-[400px]"
      hideDefaultButtons={true}
      content={
        <form className="flex flex-col gap-lg">
          <BodyText color="text-secondary">
            {formatMessage(
              {
                id: "login.customerPortalLogin.loginPrompt",
                defaultMessage: msg.login["customerPortalLogin"][
                  "loginPrompt"
                ] as string,
              },
              {
                portalName,
                link: (chunks: React.ReactNode) => (
                  <span
                    className="text-brand cursor-pointer"
                    onClick={() => navigate("/oem")}
                  >
                    {chunks}
                  </span>
                ),
              },
            )}
          </BodyText>
          <Input
            keyId="email"
            label={msg.login["email"]["label"]}
            name="email"
            type="text"
            placeholder={msg.login["email"]["placeholder"]}
            {...emailField}
            setRef={emailField.ref}
            errorMessage={errors?.email?.message?.toString()}
            customInputClasses={
              errors?.email?.message
                ? "bg-primary border-danger focus:bg-primary focus:border-danger"
                : watch("email")
                ? "bg-primary border-primary"
                : "bg-accent border-transparent"
            }
          />

          <Input
            keyId="password"
            label={msg.login["password"]["label"]}
            name="password"
            type={showPassword ? "text" : "password"}
            trailingIconConfig={{
              icon: showPassword ? (
                // @ts-ignore
                <Eye onClick={() => setShowPassword(!showPassword)} />
              ) : (
                // @ts-ignore
                <EyeSlash onClick={() => setShowPassword(!showPassword)} />
              ),
            }}
            placeholder={msg.login["password"]["placeholder"]}
            {...passwordField}
            setRef={passwordField.ref}
            errorMessage={errors?.password?.["message"]?.toString()}
            customInputClasses={
              errors?.password?.message
                ? "bg-primary border-danger focus:bg-primary focus:border-danger"
                : watch("password")
                ? "bg-primary border-primary"
                : "bg-accent border-transparent"
            }
          />
          <BodyText
            onClick={onForgotPasswordClick}
            color="text-brand"
            className="!font-bold text-right"
            size={BODY_TEXT_SIZES.X_SMALL}
          >
            <span className="cursor-pointer">
              {msg.login["forgotPassword"]["title"]}
            </span>
          </BodyText>
        </form>
      }
      footer={
        <div className="px-6 pb-6">
          <Button
            fullWidth={true}
            text={msg.customerPortals["welcome"]["login"] as string}
            className="mb-4"
            type="button"
            disabled={loading}
            onClick={handleSubmit(onSubmit, onError)}
          />
          <div className="flex flex-col items-center gap-2">
            <div className="flex">
              <BodyText color="text-secondary">
                {msg.customerPortals["welcome"]["dontHaveAccess"]}
              </BodyText>{" "}
              <BodyText
                onClick={onRequestClick}
                color="text-brand"
                className="!font-bold ml-1 cursor-pointer"
              >
                {msg.customerPortals["welcome"]["requestPortalAccess"]}
              </BodyText>
            </div>
          </div>
        </div>
      }
    />
  );
};
